<template>
  <div class="send-tokens-modal">
    <ModalFullBackdrop class="fenif-test">
      <div class="container success" v-if="tokensSended">
        <h3 class="modal-title">{{ $t("tokens_modal.title_success") }}</h3>
        <div style="margin-top: 1.3rem">
          <div
            class="picture"
            :style="{
              'background-image':
                'url(' + model.public_profile_image_path + ')',
            }"
          ></div>
          <div class="name" style="margin-top: 0.5rem">
            {{ model.name }}
          </div>
          <div class="username">
            <div>@{{ model.username }}</div>
            <img
              v-if="model.verified"
              src="/img/verified.png"
              class="verified"
              alt="Verified"
              title="Verified"
            />
          </div>
        </div>
        <div class="message_tokens">
          {{ $t("tokens_modal.descripcion_success") }}
        </div>
        <div class="tokens_quantity">{{ tokensSendedQuantity }} Tokens</div>
        <div class="footer">
          <button class="cancel" @click="cancelHandler">
            {{ $t("tokens_modal.button_back") }}
          </button>
        </div>
      </div>
      <div class="container spinner" v-if="sendingLoading">
        <Spinner />
      </div>
      <div
        class="container"
        v-if="!notEnoughTokens && !sendingLoading && !tokensSended"
      >
        <h3 class="modal-title">{{ $t("tokens_modal.title_send_tokens") }}</h3>
        <div style="display: flex; margin-top: 1.3rem">
          <div
            class="picture"
            :style="{
              'background-image':
                'url(' + model.public_profile_image_path + ')',
            }"
          ></div>
          <div class="user-card">
            <div class="name">
              <div>{{ model.name }}</div>
              <img
                v-if="model.verified"
                src="/img/verified.png"
                class="verified"
                alt="Verified"
                title="Verified"
              />
            </div>
            <div style="padding: 0 0.5rem">@{{ model.username }}</div>
          </div>
        </div>
        <!-- {{ $t("tokens_modal.send_to_user") }} -->
        <div class="form-input">
          <div
            style="
              margin-top: 1rem;
              font-weight: bold;
              text-transform: uppercase;
            "
          >
            Tokens Disponibles:
            <template v-if="balanceLoaded">{{ balance }}</template>
          </div>
        </div>
        <div class="form-input">
          <fieldset>
            <legend>{{ $t("tokens_modal.quantity") }}</legend>
            <input
              type="number"
              name="tokens"
              :min="minTokens"
              v-model="sendTokensForm.tokens"
            />
          </fieldset>
          <template v-if="$v.tokens.$error">
            <p class="error-message" v-if="!$v.tokens.required">
              {{ $t("validations.required", { field: $t("fields.tokens") }) }}
            </p>
            <p class="error-message" v-if="!$v.tokens.minValue">
              {{
                $t("validations.minValue", {
                  field: $t("fields.tokens"),
                  minValue: minTokens,
                })
              }}
            </p>
            <p class="error-message" v-if="!$v.tokens.integer">
              {{ $t("validations.integer", { field: $t("fields.tokens") }) }}
            </p>
          </template>
        </div>

        <div class="form-input">
          <fieldset>
            <legend>{{ $t("tokens_modal.message") }}</legend>
            <textarea
              rows="6"
              v-model="sendTokensForm.message"
              maxlength="200"
            ></textarea>
          </fieldset>
          <div style="text-align: right; padding: 0.3rem 0; color: #767676">
            {{ sendTokensForm.message.length }}/{{ maxMessage }}
          </div>
          <template v-if="$v.message.$error">
            <p
              class="error-message"
              v-if="!$v.message.maxLength"
            >
              {{
                $t("validations.maxLength", {
                  field: $t("fields.message"),
                  characters: maxMessage,
                })
              }}
            </p>
          </template>
        </div>
        <div class="footer">
          <button class="cancel active" @click="cancelHandler">
            {{ $t("tokens_modal.button_cancel") }}
          </button>
          <button @click="sendTokensHandler">
            {{ $t("tokens_modal.button_send") }}
          </button>
        </div>
      </div>
      <div class="container" v-if="notEnoughTokens">
        <h3 class="modal-title" style="color: red">
          {{ $t("tokens_modal.title_not_enough_tokens") }}
        </h3>
        <p
          style="
            font-weight: bold;
            font-size: 1.2rem;
            text-transform: uppercase;
          "
        >
          {{ $t("tokens_modal.description_not_enought_tokens") }}
        </p>
        <div class="footer">
          <button class="cancel active" @click="cancelHandler">
            {{ $t("tokens_modal.button_back") }}
          </button>
          <router-link class="action" :to="{ name: 'purchase.tokens' }">
            <button>{{ $t("tokens_modal.button_buy_tokens") }}</button>
          </router-link>
        </div>
      </div>
    </ModalFullBackdrop>
  </div>
</template>

<script setup lang="ts">
import { required, minValue, maxLength, integer } from "@vuelidate/validators";
import { useVuelidate } from "@vuelidate/core";
import resources from "@/utils/resources";

import { Model } from "@/types/Model";
import { Post } from "@/types/Post";

const $emit = defineEmits(["cancel"]);

const { model, post } = defineProps<{
  model: Model;
  post: Post;
}>();

// Constants
const minTokens = +(useRuntimeConfig().public.VUE_APP_TOKENS_MIN_TOKEN || 0);
const maxMessage = 200;

// Data
const notEnoughTokens = ref(false);
const sendingLoading = ref(false);
const tokensSended = ref(false);
const balanceLoaded = ref(false);
const sendTokensForm = reactive({
  tokens: 0,
  message: "",
});
const tokensSendedQuantity = ref(0);
const balance = ref(0);

const rules = {
  tokens: {
    required,
    minValue: minValue(minTokens),
    integer,
  },
  message: {
    maxLength: maxLength(maxMessage),
  },
};

const $v = useVuelidate(rules, sendTokensForm);

function cancelHandler() {
  $emit("cancel");
}

function getTokensBalance() {
  resources.tokens.balance().then(({ data }) => {
    balance.value = data.balance;
    balanceLoaded.value = true;
  });
}

function sendTokensHandler() {
  if ($v.value.$invalid) {
    return $v.value.sendTokensForm.$touch();
  }

  sendingLoading.value = true;
  resources.tokens
    .store({
      model_id: model.id,
      tokens: sendTokensForm.tokens,
      message: sendTokensForm.message,
      post_id: post.id,
      post_type: post.component,
      type: "no_post_block",
    })
    .then((data) => {
      if (data == "ok") {
        tokensSended.value = true;
        tokensSendedQuantity.value = sendTokensForm.tokens;
      }

      sendTokensForm.tokens = minTokens;
      sendTokensForm.message = "";
    })
    .catch((err) => {
      if (err.data.error && err.data.error == "Not enough tokens") {
        notEnoughTokens.value = true;
      }
    })
    .finally(() => {
      sendingLoading.value = false;
    })
}

onMounted(() => {
  getTokensBalance();
});
</script>

<style lang="scss" scoped>
.send-tokens-modal {
  & .spinner {
    align-items: center;
    display: flex;
    min-width: 300px;
    min-height: 300px;
    justify-content: center;
  }

  & .picture {
    width: 5rem;
    height: 5rem;
    margin: 0 auto;
    background-repeat: no-repeat;
    background-position: center;
    background-size: cover;
    background-color: #ffffff;
    border-radius: 50%;
    border: 3px solid var(--pallete_main_color);
  }

  & .verified {
    height: 1.2rem;
    width: 1.2rem;
    margin-left: 0.5rem;
  }

  & .container {
    box-sizing: border-box;
    border-radius: 1rem;
    border: solid 2px #ccc;
    padding: 1rem;

    @media (min-width: 768px) {
      min-width: 350px;
    }

    & .modal-title {
      font-size: 1.5rem;
      text-transform: uppercase;
    }

    & h3 {
      margin: 0.5rem 0;
    }

    & .form-input {
      margin: 0.5rem 0;

      & .error-message {
        color: var(--pallete_main_color);
        font-size: 0.9rem;
        padding: 0.3rem;
        margin: 0;
      }
    }

    & .footer {
      display: flex;
      justify-content: flex-end;

      & button {
        @extend %secondary-btn;
      }

      & button,
      .action {
        border-radius: 0.5rem;
        margin-left: 1rem;
        font-size: 1rem;
        font-weight: bold;
        text-transform: uppercase;
        padding: 0.5rem 1rem;
      }

      & .action {
        padding: 0;
      }

      & .action > button {
        margin-left: 0;
      }

      & button.cancel {
        @extend %fresh-btn;

        border-radius: 0.5rem;
      }
    }

    & label {
      box-sizing: border-box;
      font-weight: bold;
      padding: 0 1rem 0 0;
    }

    & textarea {
      border: none;
      max-width: 100%;
      min-width: 100%;
      padding: 0.3rem 0.5rem 0.8rem 0.5rem;
      font-size: 1rem;
    }

    & textarea:focus {
      border: transparent;
      outline: none;
    }

    & input {
      border: none;
      border-radius: 1rem;
      font-size: 1rem;
      padding: 0.3rem 0.5rem 0.8rem 0.5rem;
      width: 100%;
    }

    & input:focus {
      border: transparent;
      outline: none;
    }

    & fieldset {
      border-color: #ffffff7d;
      border-radius: 0.5rem;
      padding: 0;
      margin-top: 1.5rem;

      & legend {
        color: #767676;
        font-size: 1rem;
        margin-left: 0.3rem;
        font-weight: bold;
        text-transform: uppercase;
      }
    }

    & fieldset:has(> input:focus),
    & fieldset:has(> textarea:focus) {
      border-color: var(--pallete_main_color);
    }
  }

  & .success {
    text-align: center;

    & h3 {
      color: green;
    }

    & .picture {
      height: 6rem;
      width: 6rem;
    }

    & .cancel {
      font-size: 1.2rem;
      font-weight: bold;
    }

    & .footer {
      justify-content: center;
      margin-top: 1rem;
    }

    & .name {
      font-size: 1.2rem;
      text-transform: uppercase;
      padding: 0 0.5rem;
      font-weight: bold;
    }

    & .username {
      padding: 0 0.5rem;
      display: flex;
      align-items: center;
      justify-content: center;
      margin: 0.3rem 0;

      & .verified {
        height: 1rem;
        width: 1rem;
        margin-left: 0.2rem;
      }
    }

    & .message_tokens {
      font-size: 1.5rem;
      font-weight: bold;
      margin: 1rem 0 0.5rem 0;
      text-transform: uppercase;
    }

    & .tokens_quantity {
      font-size: 2.5rem;
      color: red;
      font-weight: bold;
    }
  }

  & .user-card {
    display: flex;
    flex: 1;
    flex-direction: column;
    justify-content: space-evenly;

    & .name {
      font-size: 1.2rem;
      text-transform: uppercase;
      padding: 0 0.5rem;
      font-weight: bold;
      display: flex;
    }
  }
}
</style>
